import { Card, TextField, Button, CircularProgress, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import React, { ReactNode } from 'react';
import { adminGetItems, deleteItem, getItem, saveItem } from '../../sp-firebase/genericFB';
import BasicTable, { Row, BasicColumn } from '../FinancialPositions/components/BasicTable';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { EXPOSURE_HISTORY } from '../../sp-firebase/exposureHistoryFB';

const NONE = 'none';

export const AdminDataGames = () => {
  const [collectionName, setCollectionName] = React.useState<string>('balance-instructions');
  const [itemId, setItemId] = React.useState<string>('1');
  const [itemJson, setItemJson] = React.useState<string>('');
  const [showItemJson, setShowItemJson] = React.useState<boolean>(false);
  const [gridItems, setGridItems] = React.useState<any[]>([]);
  const [dirty, setDirty] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const filters = [NONE, 'materialId', 'accountId', 'id'];
  const [filterBy, setFilterBy] = React.useState<string>(filters[0]);
  const [filterVal, setFilterVal] = React.useState<string>('');
  const [filterOperand, setFilterOperand] = React.useState<string>('Contains');

  const getItemById = async (id?: string) => {
    setLoading(true);

    const item = await getItem<any>(id || itemId, collectionName);
    setItemJson(JSON.stringify(item, null, 2));
    setShowItemJson(true);

    setDirty(false);
    setLoading(false);
  };

  const createItem = async () => {
    setLoading(true);

    if (!confirm('you sure you want to create new item?')) {
      setLoading(false);
      return;
    }

    const item = fixTimestampRecursive(JSON.parse(itemJson));

    const createdItem = await saveItem<any>(itemId, item, collectionName);
    setItemJson(JSON.stringify(createdItem, null, 2));
    setShowItemJson(true);
    setLoading(false);
  };

  const isTimestamp = (value: any): boolean =>
    typeof value === 'object' && value !== null && 'seconds' in value && typeof value.seconds === 'number';

  function fixTimestampRecursive(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => fixTimestampRecursive(item));
    } else if (typeof obj === 'object' && obj !== null) {
      return Object.entries(obj).reduce(
        (res, [key, value]: [string, any]) => ({
          ...res,
          [key]: isTimestamp(value) ? new Timestamp(value?.seconds, 0) : fixTimestampRecursive(value),
        }),
        {},
      );
    }
    return obj;
  }

  const updateItem = async () => {
    setLoading(true);

    const item = fixTimestampRecursive(JSON.parse(itemJson));

    const savedItem = await saveItem<any>(itemId, item, collectionName);
    setItemJson(JSON.stringify(savedItem, null, 2));
    setShowItemJson(true);
    setLoading(false);
  };

  const deleteRow = async () => {
    setLoading(true);

    if (!confirm(`you sure you want to delete: ${itemId}?`)) {
      setLoading(false);
      return;
    }

    await deleteItem(itemId, collectionName);

    setLoading(false);
  };

  const deleteAll = async () => {
    setLoading(true);

    if (!confirm(`you sure you want to delete all items visible ?? ${collectionName}?`)) {
      setLoading(false);
      return;
    }

    const res = await Promise.all(gridItems?.map((item) => deleteItem(item.firebaseId, collectionName)) || []);
    console.log('deleted items', res.length);

    getAll();
  };

  const dynamicFilter = (item: any, field: string, operand: string, value: string): boolean =>
    operand === 'Contains' ? item[field]?.includes(value) : item[field] === value;

  const getAll = async () => {
    setLoading(true);

    const allItems = await adminGetItems<any>(collectionName);

    const visibleVals =
      filterBy !== NONE ? allItems?.filter((i) => dynamicFilter(i, filterBy, filterOperand, filterVal)) : allItems;

    setGridItems(visibleVals || [{ noData: 'no data found' }]);

    setLoading(false);
  };

  const clickItemId = (id: string) => {
    setItemId(id);
    getItemById(id);
  };

  const genericFormatter = (data: Row): ReactNode | undefined => {
    if (data?.value instanceof Timestamp) {
      const value = data?.value as Timestamp;
      return <span>{value.toDate().toLocaleDateString('en-CA')}</span>;
    }
    if (data?.value instanceof Object) {
      return <span>{JSON.stringify(data?.value)}</span>;
    }
    return undefined;
  };

  const getColDefs = (row: any): BasicColumn[] => [
    {
      name: 'ID',
      value: 'firebaseId',
      formatter: (data: any) => (
        <div onClick={() => clickItemId(data?.value)} className="text-blue-600 cursor-pointer hover:underline">
          {data?.value}
        </div>
      ),
    },
    ...Object.keys(row)
      .filter((r) => r !== 'firebaseId')
      .sort()
      .map((k) => ({ name: k, value: k, formatter: genericFormatter })),
  ];

  const collections = [
    'accounts',
    'instruments',
    'base-instruments',
    'balance-flow',
    'balance-instructions',
    'financial-positions',
    'future-orders',
    'future-orders-exposure',
    'InventoryReports',
    'exposure',
    EXPOSURE_HISTORY,
    'routes',
    'route-policies',
    'materials',
    'users',
    'Notifications',
    'history-prices',
    'product',
    'file-batches',
    'notification-rules',
  ];

  const operands = ['Contains', 'Equals'];
  return (
    <div className="w-full h-full bg-white flex flex-col">
      <Card className="flex flex-row gap-4 align-top min-h-16 m-1 p-4">
        <Link to=".." className="decoration-blue-600 cursor-pointer">
          &lt;- Go back
        </Link>
        <TextField
          onChange={({ target }) => {
            setDirty(true);
            setCollectionName(target.value);
          }}
          value={collectionName}
          label="Collection name"
        ></TextField>
        <TextField
          value={itemId}
          onChange={({ target }) => {
            setDirty(true);
            setItemId(target.value);
          }}
          label="item id"
        ></TextField>
        <div className="flex flex-col">
          <p className="text-xs">collection name:</p>
          <Select
            className="h-10"
            label="collections"
            onChange={({ target }: SelectChangeEvent<string>) => setCollectionName(target?.value || '')}
          >
            {collections.map((c, i) => (
              <MenuItem key={i} id={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className="flex flex-col">
          <p className="text-xs">filter by:</p>
          <Select
            className="h-10"
            label="filter by"
            onChange={({ target }: SelectChangeEvent<string>) => setFilterBy(target?.value || '')}
            value={filterBy}
          >
            {filters.map((c, i) => (
              <MenuItem key={i} id={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </div>
        {filterBy !== NONE && (
          <>
            <div className="flex flex-col">
              <p className="text-xs">operand:</p>
              <Select
                className="h-10"
                label="operand"
                onChange={({ target }: SelectChangeEvent<string>) => setFilterOperand(target?.value || '')}
                value={filterOperand}
              >
                {operands.map((c, i) => (
                  <MenuItem key={i} id={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <TextField
              value={filterVal}
              onChange={({ target }) => {
                setDirty(true);
                setFilterVal(target.value);
              }}
              label="Filter value"
            ></TextField>
          </>
        )}
        <Button onClick={getAll} variant="contained">
          {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
          Get all (limit 200)
        </Button>
      </Card>
      <Card className="flex flex-row gap-4 align-top m-1 p-1 min-h-64">
        {!!gridItems?.length && (
          <div className="max-h-[36rem] overflow-auto">
            <BasicTable
              allRows={gridItems}
              columns={getColDefs(gridItems[0])}
              options={{
                cellClass: 'max-h-24 overflow-scroll',
                noBorders: true,
              }}
            />
          </div>
        )}
      </Card>
      {itemJson?.length > 0 && (
        <div className="flex flex-col">
          <div>
            collection: <b>{collectionName}</b> firebaseId: <b>{itemId}</b>
          </div>
          <div className="flex flex-row">
            <Button onClick={() => setShowItemJson((isShown) => !isShown)}>{showItemJson ? 'hide' : 'show'}</Button>
            {showItemJson && (
              <TextField
                multiline
                value={itemJson}
                onChange={({ target }) => setItemJson(target.value)}
                className="w-[90%] m-4"
              />
            )}
          </div>
        </div>
      )}
      <Card className="flex flex-row gap-4 align-top min-h-16 m-2 p-2">
        {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
        <Button onClick={createItem} className="w-40" variant="contained">
          Create item
        </Button>
        <Button onClick={() => getItemById()} className="w-40" variant="contained">
          Read item
        </Button>
        <Button
          onClick={updateItem}
          className="w-40"
          variant="contained"
          disabled={!itemId?.length || !itemJson?.length || dirty}
        >
          Update item
        </Button>
        <Button onClick={deleteRow} className="w-40" variant="contained">
          Delete item
        </Button>
        <Button onClick={deleteAll} disabled={gridItems?.length < 2} className="w-40" variant="contained">
          Delete ALL
        </Button>
        {/* <Button onClick={genRouteInstruments} className="w-40" variant="contained">
          fix
        </Button> */}
      </Card>
    </div>
  );
};
